// Polish notation without grouping: a decimal comma, never a thousands separator (0111)
const DISPLAY_FORMAT = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 3, useGrouping: false });
const INPUT_FORMAT = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 6, useGrouping: false });

/**
 * An amount with its unit, the one way the app prints a quantity: the number, a space,
 * the unit code — "20 szt", "12,987 in", "55,8 m". At most three decimals, trailing zeros
 * dropped, a decimal comma (0111); nothing here converts between units.
 */
export function formatAmount(amount: number, unit: string): string {
  return `${formatNumber(amount)} ${unit}`;
}

/**
 * The number of formatAmount without its unit, for the places that print a bare count
 * beside a unit printed once (the progress "18,6 / 55,8 m", a stage cell).
 */
export function formatNumber(amount: number): string {
  const text = DISPLAY_FORMAT.format(amount);

  // -0,0001 rounds to a signed zero, which Intl prints as "-0"
  return text === '-0' ? '0' : text;
}

/**
 * A 0..1 progress fraction as Polish prints a percentage: a whole number, a space, the sign —
 * "27 %". Below 1 it never reads 100 %, so the full figure means Done (0112).
 */
export function formatPercent(fraction: number): string {
  const percent = Math.round(100 * fraction);

  return `${formatNumber(fraction < 1 ? Math.min(99, percent) : percent)} %`;
}

/**
 * What an amount input starts with: the decimal comma like everywhere else, but the six
 * decimals the API keeps — Enter on an untouched 0,0125 must not save 0,013.
 */
export function formatAmountInput(amount: number): string {
  return INPUT_FORMAT.format(amount);
}

/**
 * Reads what a user typed into an amount input: a decimal comma or a dot, either works.
 * Anything else is NaN (and "" is 0), exactly as Number() read it before — the callers'
 * "> 0" checks stay the validation.
 */
export function parseAmount(text: string): number {
  return Number(text.trim().replace(',', '.'));
}

/**
 * Doubles cannot hold 18,6 × 3 or 55,8 − 18,6 exactly; rounding a computed amount to the
 * six decimals the API stores (Amounts.Normalize) keeps a sum or a remaining from carrying
 * a 1e-15 tail into a comparison — a line that is done reads as done.
 */
export function normalizeAmount(amount: number): number {
  return Math.sign(amount) * Math.round(Math.abs(amount) * 1e6) / 1e6;
}
